import { api } from '../lib/api'

export type JobKind = 'CONTRACT' | 'EMPLOYMENT'
export type JobVisibility = 'OPEN' | 'DIRECT'
export type JobLocationMode = 'REMOTE' | 'ONSITE' | 'HYBRID'
export type JobCompensationMode = 'FIXED' | 'HOURLY' | 'SALARY'

export type JobDraftPayload = {
  job_kind: JobKind
  job_visibility: JobVisibility
  job_mode?: 'REMOTE' | 'LOCAL'
  target_pro_user?: number | null
  claimed_pro_user_id?: number | null
  claimed_at?: string | null
  matched_count?: number
  title: string
  summary?: string
  details_md?: string
  location_mode?: JobLocationMode
  location_text?: string
  client_zipcode?: string
  client_location_text?: string
  client_lat?: number | null
  client_lng?: number | null
  scheduled_for?: string | null
  urgency?: 'FLEXIBLE' | 'TODAY' | 'ASAP'
  attachments?: unknown[]
  compensation_mode?: JobCompensationMode
  min_rate_cents?: number | null
  max_rate_cents?: number | null
  fixed_price_cents?: number | null
  salary_cents?: number | null
  currency?: string
  skills?: string[]
}

export type Job = {
  id: number
  post_id: number
  created_by: number
  job_kind: JobKind
  job_visibility: JobVisibility
  job_mode?: 'REMOTE' | 'LOCAL'
  target_pro_user?: number | null
  claimed_pro_user_id?: number | null
  claimed_at?: string | null
  matched_count?: number
  pending_deliveries_count?: number
  rebroadcast_count?: number
  last_rebroadcast_at?: string | null
  can_rebroadcast?: boolean
  is_direct?: boolean
  target_pro_user_id?: number | null
  accepted_applicant_user_id?: number | null
  viewer_delivery_status?: 'PENDING' | 'CLAIMED' | 'DECLINED' | 'EXPIRED' | null
  invitation_status?: 'PENDING' | 'ACCEPTED' | 'DECLINED' | null
  invited_at?: string | null
  invite_message?: string | null
  status: 'DRAFT' | 'PUBLISHED' | 'IN_REVIEW' | 'OPEN' | 'IN_PROGRESS' | 'DELIVERED' | 'COMPLETED' | 'DISPUTED' | 'FILLED' | 'CLOSED' | 'CANCELLED'
  title: string
  summary?: string
  details_md?: string
  location_mode?: JobLocationMode
  location_text?: string
  client_zipcode?: string
  client_location_text?: string
  client_lat?: number | null
  client_lng?: number | null
  scheduled_for?: string | null
  urgency?: 'FLEXIBLE' | 'TODAY' | 'ASAP'
  attachments?: unknown[]
  accepted_at?: string | null
  declined_at?: string | null
  decline_reason?: string
  delivered_at?: string | null
  approved_at?: string | null
  completed_at?: string | null
  auto_release_at?: string | null
  dispute_opened_at?: string | null
  dispute_resolved_at?: string | null
  dispute_resolution?: 'RELEASE_TO_PRO' | 'REFUND_TO_CLIENT' | 'SPLIT' | null
  dispute_split_bps_to_pro?: number | null
  cancelled_at?: string | null
  cancel_reason?: string | null
  compensation_mode?: JobCompensationMode
  min_rate_cents?: number | null
  max_rate_cents?: number | null
  fixed_price_cents?: number | null
  salary_cents?: number | null
  currency?: string
  escrow_required?: boolean
  escrow_status?: 'NONE' | 'HELD' | 'RELEASED' | 'REFUNDED'
  escrow_amount_pixe?: number | null
  escrow_fee_pixe?: number | null
  escrow_net_pixe?: number | null
  funded_at?: string | null
  released_at?: string | null
  refunded_at?: string | null
  escrow_events_recent?: Array<{
    id: number
    event_type: string
    actor_user_id: number | null
    note?: string
    amount_pixe?: number | null
    fee_pixe?: number | null
    net_pixe?: number | null
    created_at?: string | null
  }>
  dispute_evidence_recent?: Array<{
    id: number
    submitted_by_id: number
    message: string
    attachments?: unknown[]
    created_at?: string | null
  }>
  review?: {
    id: number
    rating: number
    body?: string
    created_at?: string | null
    reviewer_user?: number
    pro_user?: number
  } | null
  proposal_count?: number
  accepted_proposal_id?: number | null
  my_proposal?: JobProposal | null
  skills_data?: Array<{ name: string; slug: string }>
  post?: any
}

export type JobProposal = {
  id: number
  job: number
  pro_user: number
  status: 'SUBMITTED' | 'WITHDRAWN' | 'ACCEPTED' | 'REJECTED'
  proposed_price_pixe?: number | null
  proposed_hourly_pixe?: number | null
  proposed_timeline_days?: number | null
  message?: string
  created_at?: string
  updated_at?: string
  pro_handle?: string
  pro_display_name?: string
  pro_rating?: number
  pro_completed_jobs_count?: number
  thread_id?: number | null
}

export type JobThreadPayload = {
  job_id: number
  thread_id: number
  thread: any
}

export async function createJobDraft(payload: JobDraftPayload) {
  const { data } = await api.post<Job>('/api/jobs/drafts/', payload)
  return data
}

export async function patchJob(id: number | string, payload: Partial<JobDraftPayload>) {
  const { data } = await api.patch<Job>(`/api/jobs/${id}/`, payload)
  return data
}

export async function publishJob(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/publish/`, {})
  return data
}

export async function getJob(id: number | string) {
  const { data } = await api.get<Job>(`/api/jobs/${id}/`)
  return data
}

export async function applyToJob(id: number | string, payload: { cover_note?: string; proposed_rate_cents?: number | null }) {
  const { data } = await api.post(`/api/jobs/${id}/apply/`, payload)
  return data
}

export async function listApplications(id: number | string) {
  const { data } = await api.get<{ results: any[] }>(`/api/jobs/${id}/applications/`)
  return data
}

export async function acceptApplication(id: number | string, appId: number | string) {
  const { data } = await api.post(`/api/jobs/${id}/applications/${appId}/accept/`, {})
  return data
}

export async function fundEscrow(id: number | string, amount_pixe: number, source: 'wallet' = 'wallet') {
  const { data } = await api.post<Job>(`/api/jobs/${id}/escrow/fund/`, { amount_pixe, source })
  return data
}

export async function releaseEscrow(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/escrow/release/`, {})
  return data
}

export async function refundEscrow(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/escrow/refund/`, {})
  return data
}

export async function acceptLocalJob(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/local/accept/`, {})
  return data
}

export async function acceptInviteJob(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/invite/accept/`, {})
  return data
}

export async function declineInviteJob(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/invite/decline/`, {})
  return data
}

export async function declineLocalJob(id: number | string, reason?: string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/local/decline/`, reason ? { reason } : {})
  return data
}

export async function claimLocalJob(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/local/claim/`, {})
  return data
}

export async function declineLocalDelivery(id: number | string, reason?: string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/local/decline/`, reason ? { reason } : {})
  return data
}

export async function rebroadcastJob(id: number | string) {
  const { data } = await api.post<Job & { created_deliveries_count?: number; pending_count_total?: number }>(
    `/api/jobs/${id}/local/rebroadcast/`,
    {},
  )
  return data
}

export async function completeJob(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/complete/`, {})
  return data
}

export async function deliverJob(id: number | string, payload?: { note?: string; attachments?: string[] }) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/deliver/`, payload || {})
  return data
}

export async function approveJob(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/approve/`, {})
  return data
}

export async function disputeJob(id: number | string, payload: { reason: string; details?: string }) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/dispute/`, payload)
  return data
}

export async function createJobReview(id: number | string, payload: { rating: number; body?: string }) {
  const { data } = await api.post(`/api/jobs/${id}/review/`, payload)
  return data
}

export async function openDispute(id: number | string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/escrow/dispute/open/`, {})
  return data
}

export async function addDisputeEvidence(id: number | string, payload: { message: string; attachments?: string[] }) {
  const { data } = await api.post(`/api/jobs/${id}/escrow/dispute/evidence/`, payload)
  return data
}

export async function cancelJob(id: number | string, reason?: string) {
  const { data } = await api.post<Job>(`/api/jobs/${id}/cancel/`, reason ? { reason } : {})
  return data
}

export async function submitProposal(
  id: number | string,
  payload: { proposed_price_pixe?: number | null; proposed_hourly_pixe?: number | null; proposed_timeline_days?: number | null; message?: string },
) {
  const { data } = await api.post<JobProposal>(`/api/jobs/${id}/proposals/`, payload)
  return data
}

export async function withdrawProposal(id: number | string) {
  const { data } = await api.post<JobProposal>(`/api/jobs/${id}/proposals/withdraw/`, {})
  return data
}

export async function listProposals(id: number | string) {
  const { data } = await api.get<{ results: JobProposal[] }>(`/api/jobs/${id}/proposals/`)
  return data
}

export async function acceptProposal(id: number | string, proposalId: number | string) {
  const { data } = await api.post<{ proposal: JobProposal; job: Job; thread_id?: number | null }>(
    `/api/jobs/${id}/proposals/${proposalId}/accept/`,
    {},
  )
  return data
}

export async function getJobThread(id: number | string) {
  const { data } = await api.get<JobThreadPayload>(`/api/jobs/${id}/thread/`)
  return data
}

export async function postJobThreadMessage(id: number | string, payload: { text?: string; attachments?: any[] }) {
  const { data } = await api.post(`/api/jobs/${id}/thread/messages/`, payload)
  return data
}
